import { publicApi, siteApi } from "@api";
import { useScrollPositionDetector } from "@components/layouts/ScrollBox/hooks";
import { useDepositDrawer } from "@components/utils/DepositDrawer/hooks";
import { openOnboardingDrawer } from "@components/utils/OnboardingDrawer/utils";
import { OnboardMode } from "@constants/misc";
import { Paths } from "@constants/paths";
import usePromotionStatus from "@hooks/usePromotionStatus";
import useUserMode from "@hooks/useUserMode";
import { Banner, BannerActionType, PropsWithClassName, cn } from "kz-ui-sdk";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useScrollOnDrag from "react-scroll-ondrag";
import { BaseBanner, GameBanner } from "./components";
import { useGameList } from "./hooks";

const INFINITE_SCROLL_MIN_THRESHOLD = 5;
const BANNER_WIDTH = 74;
const BANNER_HEIGHT = 40;
const BANNER_GAP = 6;

interface MiniPromotionBannersProps extends PropsWithClassName {
  shadowClassName?: string;
  onLengthChange?: (length: number) => void;
  infiniteScroll?: boolean;
}

const queryParams = { query: { limit: 9999, offset: 0 } };

const MiniPromotionBanners = ({
  className,
  shadowClassName,
  onLengthChange,
  infiniteScroll = false,
}: MiniPromotionBannersProps) => {
  const refListContainer = useRef<HTMLDivElement>(null);
  const { events: scrollOnDragEvents } = useScrollOnDrag(refListContainer);
  const { checkPosition, isScrollbarAtRight, isScrollbarAtLeft } = useScrollPositionDetector(refListContainer, {
    direction: "horizontal",
  });

  const [displayBanners, setDisplayBanners] = useState<Banner[]>([]);
  const originalBannersRef = useRef<Banner[]>([]);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  const promotionStatus = usePromotionStatus();

  const { isMember } = useUserMode();

  const isURLClaimable = useCallback(
    (url?: string) => {
      switch (url) {
        case "/cashback":
          return promotionStatus.isCashbackClaimable;
        case "/prizes":
          return promotionStatus.isAnyClaimable;
        case "/referral":
          return promotionStatus.isReferralClaimable;
        case "/daily-reward":
          return promotionStatus.isDailyBonusClaimable;
        case "/refer-balls":
          return promotionStatus.isReferralRewardClaimable;
        default:
          return false;
      }
    },
    [promotionStatus],
  );

  const { data, isFetching } = isMember
    ? siteApi.useGetBannersQuery(queryParams)
    : publicApi.useGetPublicBannersQuery(queryParams);

  const originalBanners = useMemo(
    () =>
      data?.entries?.filter((banner) => {
        const isDailyRewardBanner =
          banner.action?.type === BannerActionType.InternalUrl && banner.action?.url === Paths.PRIVATE.DAILY_REWARD;
        const isReferralRewardBanner =
          banner.action?.type === BannerActionType.InternalUrl && banner.action?.url === Paths.PRIVATE.REFER_BALLS;

        // Filter out daily reward banner if user is logged in, and daily bonus is not available
        if (isDailyRewardBanner && promotionStatus.shouldHideDailyBonus) {
          return false;
        }

        // Filter out referral reward banner if user is logged in, and referral reward is not available
        if (isReferralRewardBanner && promotionStatus.shouldHideReferralReward) {
          return false;
        }
        return true;
      }),
    [data, promotionStatus.shouldHideDailyBonus, promotionStatus.shouldHideReferralReward],
  );

  const isInfiniteScroll = useMemo(() => {
    return infiniteScroll && originalBanners?.length && originalBanners.length >= INFINITE_SCROLL_MIN_THRESHOLD;
  }, [originalBanners, infiniteScroll]);

  // Calculate the width of a single set of banners
  const oneSetWidth = useMemo(() => {
    const totalBanners = originalBanners?.length || 0;
    if (totalBanners < INFINITE_SCROLL_MIN_THRESHOLD) return 0;
    return (BANNER_WIDTH + BANNER_GAP) * totalBanners;
  }, [originalBanners]);

  // Setup the tripled banner array - [Left Copy][Original][Right Copy]
  useEffect(() => {
    if (originalBanners?.length) {
      originalBannersRef.current = originalBanners;
      if (isInfiniteScroll) {
        const tripleBanners = [...originalBanners, ...originalBanners, ...originalBanners];
        setDisplayBanners(tripleBanners);
      } else {
        setDisplayBanners(originalBanners);
      }
    }
  }, [originalBanners, isInfiniteScroll]);

  // Scroll to the middle set if infinite scroll is enabled
  useLayoutEffect(() => {
    if (refListContainer.current && isInfiniteScroll) {
      requestAnimationFrame(() => {
        if (refListContainer.current) {
          // scroll to center of the middle set
          const containerWidth = refListContainer.current.offsetWidth;
          refListContainer.current.scrollLeft =
            oneSetWidth * 1.5 - containerWidth / 2 - BANNER_WIDTH / 2 + BANNER_GAP * 2;
        }
      });
    }
  }, [displayBanners.length, isInfiniteScroll, oneSetWidth]);

  useEffect(() => {
    onLengthChange?.(originalBanners?.length || 0);
  }, [originalBanners?.length, onLengthChange]);

  const gameIds: string[] = useMemo(
    () =>
      originalBanners?.map((banner) => banner?.action?.gameId).filter((gameId): gameId is string => Boolean(gameId)) ||
      [],
    [originalBanners],
  );

  const { data: games, fetched: fetchedGames } = useGameList(gameIds);

  const { openDepositDrawer } = useDepositDrawer();

  const syncScrollWithAnimationFrame = useCallback((newPosition: number) => {
    // To make the scroll smooth when resetting the scroll position, sync the scroll with animation frame
    requestAnimationFrame(() => {
      if (refListContainer.current) {
        refListContainer.current.style.scrollBehavior = "auto";
        refListContainer.current.scrollLeft = newPosition;

        scrollTimeoutRef.current = setTimeout(() => {
          if (refListContainer.current) {
            refListContainer.current.style.scrollBehavior = "";
            isScrollingRef.current = false;
          }
        }, 100);
      }
    });
  }, []);

  const handleScroll = useCallback(() => {
    checkPosition();

    if (!refListContainer.current || !isInfiniteScroll || isScrollingRef.current) return;

    const currentScrollLeft = refListContainer.current.scrollLeft;

    // Reset scroll position when reaching the right threshold
    if (currentScrollLeft <= oneSetWidth * 0.5) {
      isScrollingRef.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const relativePosition = currentScrollLeft;
      const newPosition = relativePosition + oneSetWidth;

      syncScrollWithAnimationFrame(newPosition);
      // Reset scroll position when reaching the left threshold
    } else if (currentScrollLeft >= oneSetWidth * 1.5) {
      isScrollingRef.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const relativePosition = currentScrollLeft - oneSetWidth;
      const newPosition = relativePosition;

      syncScrollWithAnimationFrame(newPosition);
    }
  }, [checkPosition, isInfiniteScroll, syncScrollWithAnimationFrame, oneSetWidth]);

  useLayoutEffect(() => {
    checkPosition();
  }, [checkPosition, displayBanners?.length]);

  const renderBanner = (banner: Banner, index: number) => {
    const claimable = !isMember || isURLClaimable(banner?.action?.url);
    switch (banner?.action?.type || banner?.name) {
      case "external_url": {
        return (
          <a
            href={banner?.action?.url}
            target="_blank"
            rel="noreferrer"
            key={`${banner.id}-${index}`}
            className="relative"
            draggable={false}
          >
            <BaseBanner
              key={`${banner.id}-${index}`}
              banner={banner}
              claimable={claimable}
            />
          </a>
        );
      }
      case "internal_url": {
        return banner?.action?.url ? (
          <Link
            to={banner?.action?.url}
            key={`${banner.id}-${index}`}
            className="relative"
            draggable={false}
          >
            <BaseBanner
              claimable={claimable}
              key={`${banner.id}-${index}`}
              banner={banner}
            />
          </Link>
        ) : null;
      }
      case "register": {
        return (
          <BaseBanner
            onClick={isMember ? undefined : () => openOnboardingDrawer({ mode: OnboardMode.REGISTER })}
            key={`${banner.id}-${index}`}
            banner={banner}
            claimable={claimable}
          />
        );
      }
      case "deposit": {
        return (
          <BaseBanner
            onClick={() => {
              if (isMember) {
                openDepositDrawer();
              } else {
                openOnboardingDrawer({
                  mode: OnboardMode.REGISTER,
                });
              }
            }}
            key={`${banner.id}-${index}`}
            banner={banner}
            claimable={claimable}
          />
        );
      }
      case "game": {
        const game = games[banner?.action?.gameId || ""];
        if (!game || game.isMaintenance) return null;
        return (
          <GameBanner
            key={`${banner.id}-${index}`}
            banner={banner}
            game={game}
            claimable={claimable}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const isLoading = isFetching || !fetchedGames;
  const isDisplayLeftShadow = isInfiniteScroll || !isScrollbarAtLeft;
  const isDisplayRightShadow = isInfiniteScroll || !isScrollbarAtRight;

  if (!displayBanners?.length) return null;

  return (
    <div className={cn("relative h-[52px] w-full", className)}>
      {/* LIST CONTAINER */}
      <div
        key={"micro-banner-container-" + displayBanners.length}
        ref={refListContainer}
        {...scrollOnDragEvents}
        onScroll={handleScroll}
        className={`scrollbar-none absolute top-0 flex h-[52px] w-[calc(100%)] flex-row gap-x-1.5 overflow-x-auto px-5`}
      >
        {isLoading ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={"banner-skeleton-" + index}
                className={cn("mt-3 flex-shrink-0 rounded bg-darkBrand-900")}
                style={{ width: BANNER_WIDTH, height: BANNER_HEIGHT }}
              />
            ))}
          </>
        ) : (
          displayBanners?.map(renderBanner)
        )}
      </div>

      {/* SHADOWS */}
      <>
        {isDisplayLeftShadow && (
          <div
            className={cn(
              "pointer-events-none absolute -left-3 top-3 h-[calc(100%-theme(space.3))] w-8 animate-fade-in bg-gradient-to-r from-darkBrand-900 to-transparent",
              shadowClassName,
            )}
          />
        )}
        {isDisplayRightShadow && (
          <div
            className={cn(
              "pointer-events-none absolute right-[-1px] top-3 h-[calc(100%-theme(space.3))] w-8 animate-fade-in bg-gradient-to-l from-darkBrand-900 to-transparent",
              shadowClassName,
            )}
          />
        )}
      </>
    </div>
  );
};

export default MiniPromotionBanners;
